import { Building2, ArrowRight } from 'lucide-react';
import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { AuthPageLayout } from '../../components/layout/AuthPageLayout';
import { trackSignupPageVisit, trackSignupAbandon } from '../../utils/signupTracking';
import { IOS_APP_STORE_URL, ANDROID_PLAY_STORE_URL, MOBILE_APP_PAUSED } from '../../constants/appStore';

export default function Signup() {
  const navigate = useNavigate();
  
  // Track signup page visit
  useEffect(() => {
    trackSignupPageVisit('student');
    
    // Track abandon on page unload
    const handleBeforeUnload = () => {
      trackSignupAbandon();
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Also track abandon when component unmounts (navigation away)
      trackSignupAbandon();
    };
  }, []);

  return (
    <AuthPageLayout showBackButton backTo="/" backLabel="Landing page">
      <Helmet>
        <title>Sign Up - RollOps</title>
        <meta name="robots" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
        <meta name="googlebot" content="noindex, nofollow, noimageindex, nosnippet, noarchive, notranslate" />
        <meta name="bingbot" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
      </Helmet>

      <div className="w-full max-w-md flex flex-col items-center">
        {/* Logo and Brand */}
        <Link
          to="/"
          className="flex items-center space-x-4 mb-8 hover:opacity-80 transition-opacity"
        >
          <img 
            src="/rollops-logo.png" 
            alt="RollOps Logo" 
            className="w-24 h-24 sm:w-32 sm:h-32"
          />
          <span className="text-5xl sm:text-6xl lg:text-7xl font-bold text-[#EBFFEE]">
            Roll<span className="text-rollops-accent">O</span>ps
          </span>
        </Link>

        {/* Gym Owner CTA - Prominent at the top */}
        <Link
          to="/owner-signup"
          className="w-full mb-6 group"
        >
          <div className="bg-gradient-to-r from-[#9B1421] to-[#7A0F1A] rounded-xl p-4 flex items-center gap-4 hover:from-[#7A0F1A] hover:to-[#5A0A12] transition-all shadow-lg">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <Building2 size={24} className="text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-white">Gym Owner?</h3>
              <p className="text-sm text-white/80">Register your gym and start managing members today</p>
            </div>
            <ArrowRight size={20} className="text-white group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>

        {/* Student Signup Card */}
        <Card className="w-full border-[#504A4A] bg-[#221F1F] shadow-lg">
          <CardHeader className="space-y-1 pb-6">
            <CardTitle className="text-2xl font-bold text-center text-[#EBFFEE]">
              Student Sign Up
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-base text-[#A9A3A3] leading-relaxed text-center mb-6">
              Create your student account to join a gym
            </p>

            <Button
              onClick={() => navigate('/guided-student-onboarding')}
              variant="red"
              size="lg"
              className="w-full"
            >
              Get Started
            </Button>

            <div className="mt-6 space-y-4">
              {!MOBILE_APP_PAUSED && (
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={() => window.open(IOS_APP_STORE_URL, '_blank')}
                    variant="outline"
                    size="lg"
                    className="flex-1 border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232] hover:text-[#EBFFEE] bg-transparent py-6 px-12 text-sm font-semibold"
                  >
                    Download for iOS
                  </Button>
                  <Button
                    onClick={() => window.open(ANDROID_PLAY_STORE_URL, '_blank')}
                    variant="outline"
                    size="lg"
                    className="flex-1 border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232] hover:text-[#EBFFEE] bg-transparent py-6 px-12 text-sm font-semibold"
                  >
                    Download for Android
                  </Button>
                </div>
              )}

              <div className="flex justify-center pt-2">
                <span className="text-sm text-[#A9A3A3]">
                  Already have an account?{' '}
                  <Link
                    to="/signin"
                    className="text-rollops-accent hover:text-[#EBFFEE] font-medium transition-colors"
                  >
                    Log In
                  </Link>
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AuthPageLayout>
  );
}
